import { useEffect, useState, useMemo, useCallback } from 'react';
import { FiUsers, FiPlus, FiEdit2, FiServer, FiX, FiGrid, FiTrash2, FiSave, FiFilter, FiSearch } from 'react-icons/fi';
import api from '../services/api';
import { toast } from 'react-toastify';
import { formatDateTime } from '../utils/dateFormat';
import Pagination from '../components/Pagination';
import { CRUD_UI } from '../components/crudUi';
import { ALL_MENU_KEYS, MENU_KEY_LABEL, MENU_SECTIONS } from '../config/menuCatalog';

const ROLE_OPTIONS = [
  { value: 'SuperAdmin', label: 'SuperAdmin (toàn hệ thống)' },
  { value: 'CloudAdmin', label: 'CloudAdmin (quản lý một số site)' },
  { value: 'CloudAPI', label: 'CloudAPI (Full API integration)' },
  { value: 'LocalAPI', label: 'LocalAPI (integration only)' },
  { value: 'LocalUser', label: 'LocalUser' },
];

const SINGLE_SITE_ROLES = new Set(['LocalAPI', 'LocalUser']);

const Users = () => {
  const [users, setUsers] = useState([]);
  const [sites, setSites] = useState([]);
  const [menuCatalog, setMenuCatalog] = useState(ALL_MENU_KEYS);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [formData, setFormData] = useState({
    username: '',
    display_name: '',
    email: '',
    password: '',
    role: 'LocalUser',
    site_ids: [],
    menu_keys: [],
  });
  const [formErrors, setFormErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});

  useEffect(() => {
    fetchUsers();
    fetchSites();
    fetchMenuCatalog();
  }, []);

  const fetchSites = async () => {
    try {
      const response = await api.get('/orthanc/sites');
      setSites(response.data.data || []);
    } catch (error) {
      console.error('Error fetching sites:', error);
    }
  };

  const fetchMenuCatalog = async () => {
    try {
      const response = await api.get('/users/meta/menus');
      setMenuCatalog(response.data?.data || ALL_MENU_KEYS);
    } catch {
      setMenuCatalog(ALL_MENU_KEYS);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data.data || []);
    } catch {
      toast.error('Không thể tải danh sách người dùng');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      username: '',
      display_name: '',
      email: '',
      password: '',
      role: 'LocalUser',
      site_ids: [],
      menu_keys: [],
    });
    setFormErrors({});
    setTouchedFields({});
    setEditingUser(null);
  };

  const isSuperAdmin = formData.role === 'SuperAdmin';
  const isSingleSiteRole = SINGLE_SITE_ROLES.has(formData.role);

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const validateUserField = (field, value, draft = formData) => {
    if (field === 'username') {
      if (editingUser) return '';
      if (!value?.trim()) return 'Username là bắt buộc';
      if (value.trim().length < 3) return 'Username tối thiểu 3 ký tự';
      return '';
    }
    if (field === 'display_name') {
      if (!value?.trim()) return 'Display name là bắt buộc';
      if (value.trim().length < 3) return 'Display name tối thiểu 3 ký tự';
      if (value.trim().length > 100) return 'Display name tối đa 100 ký tự';
      return '';
    }
    if (field === 'email') {
      if (!value?.trim()) return 'Email là bắt buộc';
      if (!isValidEmail(value.trim())) return 'Email không hợp lệ';
      return '';
    }
    if (field === 'password') {
      if (!editingUser && !value) return 'Mật khẩu là bắt buộc';
      if (value && value.length < 6) return 'Mật khẩu tối thiểu 6 ký tự';
      return '';
    }
    if (field === 'role') {
      if (!value) return 'Vai trò là bắt buộc';
      return '';
    }
    if (field === 'site_ids') {
      const role = draft.role;
      const siteIds = draft.site_ids || [];
      if (role === 'SuperAdmin') return '';
      if ((role === 'LocalAPI' || role === 'LocalUser') && siteIds.length !== 1) {
        return 'Vai trò này yêu cầu đúng 1 site';
      }
      if (siteIds.length === 0) return 'Vui lòng chọn ít nhất 1 site';
      return '';
    }
    return '';
  };

  const validateUserForm = (draft = formData) => {
    const errors = {};
    const fields = ['username', 'display_name', 'email', 'password', 'role', 'site_ids'];
    fields.forEach((field) => {
      const error = validateUserField(field, draft[field], draft);
      if (error) errors[field] = error;
    });
    return errors;
  };

  const updateField = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      setFormErrors((prevErrors) => {
        const nextErrors = { ...prevErrors };
        const error = validateUserField(field, value, next);
        if (error) nextErrors[field] = error;
        else delete nextErrors[field];

        if (field === 'role' || field === 'site_ids') {
          const siteError = validateUserField('site_ids', next.site_ids, next);
          if (siteError) nextErrors.site_ids = siteError;
          else delete nextErrors.site_ids;
        }
        return nextErrors;
      });
      return next;
    });
  };

  const normalizePayload = () => {
    let siteIds = [...(formData.site_ids || [])];
    if (isSuperAdmin) siteIds = [];
    if (isSingleSiteRole && siteIds.length > 1) siteIds = [siteIds[0]];

    const menuKeys = isSuperAdmin ? menuCatalog : (formData.menu_keys || []);

    const payload = {
      username: formData.username,
      display_name: formData.display_name,
      email: formData.email,
      role: formData.role,
      site_ids: siteIds,
      menu_keys: menuKeys,
    };

    if (formData.password) payload.password = formData.password;
    if (!payload.site_ids || payload.site_ids.length === 0) delete payload.site_ids;

    return payload;
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const errors = validateUserForm();
      setFormErrors(errors);
      setTouchedFields({ username: true, display_name: true, email: true, password: true, role: true, site_ids: true });
      if (Object.keys(errors).length > 0) {
        toast.error('Vui lòng kiểm tra lại dữ liệu');
        return;
      }

      const payload = normalizePayload();
      if (!payload.password) {
        toast.error('Mật khẩu là bắt buộc khi tạo mới');
        return;
      }
      await api.post('/users', payload);
      toast.success('Tạo người dùng thành công');
      setShowModal(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.error?.message || 'Không thể tạo người dùng');
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      display_name: user.display_name || user.username || '',
      email: user.email,
      password: '',
      role: user.role,
      site_ids: user.site_ids || (user.site_id ? [user.site_id] : []),
      menu_keys: user.menu_keys || [],
    });
    setFormErrors({});
    setTouchedFields({});
    setShowModal(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      const errors = validateUserForm();
      setFormErrors(errors);
      setTouchedFields({ username: true, display_name: true, email: true, password: true, role: true, site_ids: true });
      if (Object.keys(errors).length > 0) {
        toast.error('Vui lòng kiểm tra lại dữ liệu');
        return;
      }

      const payload = normalizePayload();
      if (!payload.password) delete payload.password;
      await api.put(`/users/${editingUser.id}`, payload);
      toast.success('Cập nhật người dùng thành công');
      setShowModal(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.error?.message || 'Không thể cập nhật người dùng');
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Bạn có chắc chắn muốn xóa người dùng "${user.username}"?`)) {
      return;
    }

    try {
      await api.delete(`/users/${user.id}`);
      toast.success('Xóa người dùng thành công');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.error?.message || 'Không thể xóa người dùng');
    }
  };

  const toggleSite = (siteId) => {
    setFormData((prev) => {
      const current = prev.site_ids || [];
      let next;
      if (SINGLE_SITE_ROLES.has(prev.role)) {
        next = { ...prev, site_ids: [siteId] };
      } else if (current.includes(siteId)) {
        next = { ...prev, site_ids: current.filter((id) => id !== siteId) };
      } else {
        next = { ...prev, site_ids: [...current, siteId] };
      }

      setFormErrors((prevErrors) => {
        const nextErrors = { ...prevErrors };
        const siteError = validateUserField('site_ids', next.site_ids, next);
        if (siteError) nextErrors.site_ids = siteError;
        else delete nextErrors.site_ids;
        return nextErrors;
      });

      return next;
    });
  };

  const toggleMenu = (menuKey) => {
    setFormData((prev) => {
      const current = prev.menu_keys || [];
      if (current.includes(menuKey)) {
        return { ...prev, menu_keys: current.filter((k) => k !== menuKey) };
      }
      return { ...prev, menu_keys: [...current, menuKey] };
    });
  };

  const getRoleBadge = (role) => {
    const config = {
      'SuperAdmin': 'bg-indigo-100 text-indigo-700',
      'CloudAdmin': 'bg-emerald-100 text-emerald-700',
      'CloudAPI': 'bg-purple-100 text-purple-700',
      'LocalAPI': 'bg-slate-100 text-slate-700',
      'LocalUser': 'bg-blue-100 text-blue-700',
    };
    return <span className={`inline-flex px-2 py-0.5 rounded text-xs font-semibold ${config[role] || 'bg-slate-100 text-slate-700'}`}>{role}</span>;
  };

  const filteredUsers = useMemo(() => {
    let data = [...users];

    if (filterRole) {
      data = data.filter((user) => user.role === filterRole);
    }

    if (searchTerm.trim()) {
      const search = searchTerm.trim().toLowerCase();
      data = data.filter((user) =>
        user.username?.toLowerCase().includes(search) ||
        user.display_name?.toLowerCase().includes(search) ||
        user.email?.toLowerCase().includes(search)
      );
    }

    return data;
  }, [users, searchTerm, filterRole]);

  const totalItems = filteredUsers.length;
  const paginatedUsers = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredUsers.slice(startIndex, endIndex);
  }, [filteredUsers, currentPage, itemsPerPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterRole]);

  const handleItemsPerPageChange = useCallback((value) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  }, []);

  if (loading) {
    return (
      <div className={CRUD_UI.loadingWrap}>
        <div className={CRUD_UI.loadingSpinner}></div>
        <p className="text-slate-500 text-sm">Đang tải danh sách người dùng...</p>
      </div>
    );
  }

  return (
    <div className={CRUD_UI.page}>
      <div className={CRUD_UI.filterCard}>
        <div className={`${CRUD_UI.filterHeader} justify-between flex-wrap gap-2`}>
          <div className="flex items-center gap-2">
            <FiFilter className="text-slate-500 text-xl" />
            <span className="text-sm font-semibold text-slate-900">Tìm kiếm và bộ lọc</span>
          </div>
          <button
            className={CRUD_UI.primaryButton}
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
          >
            <FiPlus /> Tạo người dùng
          </button>
        </div>
        <div className="flex gap-4 flex-wrap">
          <div className={CRUD_UI.filterFieldWide}>
            <label className={CRUD_UI.filterLabel}>Tìm kiếm</label>
            <div className={CRUD_UI.searchWrap}>
              <FiSearch className={CRUD_UI.searchIcon} />
              <input
                type="text"
                className={CRUD_UI.searchInput}
                placeholder="Tìm theo username, display name hoặc email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button
                  className={CRUD_UI.searchClearButton}
                  onClick={() => setSearchTerm('')}
                  title="Xóa tìm kiếm"
                >
                  <FiX className="text-base" />
                </button>
              )}
            </div>
          </div>

          <div className={CRUD_UI.filterField}>
            <label className={CRUD_UI.filterLabel}>Vai trò</label>
            <select
              className={CRUD_UI.filterSelect}
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
            >
              <option value="">Tất cả vai trò</option>
              {ROLE_OPTIONS.map((item) => (
                <option key={item.value} value={item.value}>{item.value}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className={CRUD_UI.tableViewportShell}>
        <div className={CRUD_UI.tableViewportScroll}>
          <table className={CRUD_UI.table}>
            <thead className={CRUD_UI.tableHead}>
              <tr>
                <th className={CRUD_UI.tableHeaderCell}>Username</th>
                <th className={CRUD_UI.tableHeaderCell}>Display name</th>
                <th className={CRUD_UI.tableHeaderCell}>Email</th>
                <th className={CRUD_UI.tableHeaderCell}>Role</th>
                <th className={CRUD_UI.tableHeaderCell}>Last login</th>
                <th className={CRUD_UI.tableHeaderCell}>user_site</th>
                <th className={CRUD_UI.tableHeaderCell}>user_menu</th>
                <th className={CRUD_UI.tableHeaderCell}>Action</th>
              </tr>
            </thead>
            <tbody>
              {paginatedUsers.length === 0 ? (
                <tr>
                  <td colSpan="8" className={CRUD_UI.emptyCell}>
                    <div className="flex flex-col items-center gap-2">
                      <FiUsers className="text-5xl text-slate-300" />
                      <p className="text-slate-500 text-sm">Chưa có người dùng nào</p>
                    </div>
                  </td>
                </tr>
              ) : (
                paginatedUsers.map((user) => {
                  const userSites = user.site_ids || [];
                  const siteNames = userSites.map((siteId) => {
                    const site = sites.find((s) => s.id === siteId);
                    return site ? site.name : siteId;
                  });
                  return (
                    <tr key={user.id} className={CRUD_UI.tableRow}>
                      <td className="px-3 py-2 text-sm text-slate-900 font-semibold">{user.username}</td>
                      <td className="px-3 py-2 text-sm text-slate-700">{user.display_name || '-'}</td>
                      <td className="px-3 py-2 text-sm text-slate-700">{user.email}</td>
                      <td className="px-3 py-2 text-sm">{getRoleBadge(user.role)}</td>
                      <td className="px-3 py-2 text-sm text-slate-700">{user.last_login ? formatDateTime(user.last_login) : 'Chưa đăng nhập'}</td>
                      <td className="px-3 py-2 text-sm text-slate-700">{user.role === 'SuperAdmin' ? 'All sites' : (siteNames.join(', ') || '-')}</td>
                      <td className="px-3 py-2 text-sm text-slate-700">{Array.isArray(user.menu_keys) ? user.menu_keys.length : 0} menu(s)</td>
                      <td className="px-3 py-2 text-sm">
                        <div className="flex items-center gap-1">
                          <button className={CRUD_UI.iconButtonEdit} onClick={() => handleEdit(user)} title="Sửa">
                            <FiEdit2 />
                          </button>
                          <button className={CRUD_UI.iconButtonDelete} onClick={() => handleDelete(user)} title="Xóa">
                            <FiTrash2 />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {totalItems > 0 && (
        <div className={CRUD_UI.paginationDock}>
          <Pagination
            currentPage={currentPage}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={handleItemsPerPageChange}
            itemsPerPageOptions={[10, 20, 50, 100]}
          />
        </div>
      )}

      {showModal && (
        <div className={CRUD_UI.modalOverlay}>
          <div className={CRUD_UI.modalContainer} onClick={(e) => e.stopPropagation()}>
            <div className={CRUD_UI.modalHeader}>
              <h2 className={CRUD_UI.modalTitle}>{editingUser ? 'Sửa người dùng' : 'Tạo người dùng'}</h2>
              <button className={CRUD_UI.modalCloseButton} onClick={() => { setShowModal(false); resetForm(); }}><FiX /></button>
            </div>

            <form onSubmit={editingUser ? handleUpdate : handleCreate} className={`${CRUD_UI.modalForm} space-y-4`}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Username</label>
                  <input
                    type="text"
                    className={`w-full px-3 py-2 border rounded ${touchedFields.username && formErrors.username ? 'border-red-500' : 'border-slate-300'}`}
                    value={formData.username}
                    onChange={(e) => updateField('username', e.target.value)}
                    onBlur={() => setTouchedFields((prev) => ({ ...prev, username: true }))}
                    required
                    disabled={!!editingUser}
                  />
                  {touchedFields.username && formErrors.username && <p className="mt-1 text-xs text-red-600">{formErrors.username}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Display name</label>
                  <input
                    type="text"
                    className={`w-full px-3 py-2 border rounded ${touchedFields.display_name && formErrors.display_name ? 'border-red-500' : 'border-slate-300'}`}
                    value={formData.display_name}
                    onChange={(e) => updateField('display_name', e.target.value)}
                    onBlur={() => setTouchedFields((prev) => ({ ...prev, display_name: true }))}
                    required
                  />
                  {touchedFields.display_name && formErrors.display_name && <p className="mt-1 text-xs text-red-600">{formErrors.display_name}</p>}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                  <input
                    type="email"
                    className={`w-full px-3 py-2 border rounded ${touchedFields.email && formErrors.email ? 'border-red-500' : 'border-slate-300'}`}
                    value={formData.email}
                    onChange={(e) => updateField('email', e.target.value)}
                    onBlur={() => setTouchedFields((prev) => ({ ...prev, email: true }))}
                    required
                  />
                  {touchedFields.email && formErrors.email && <p className="mt-1 text-xs text-red-600">{formErrors.email}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Password {editingUser && <span className="text-slate-400">(để trống nếu không đổi)</span>}</label>
                  <input
                    type="password"
                    className={`w-full px-3 py-2 border rounded ${touchedFields.password && formErrors.password ? 'border-red-500' : 'border-slate-300'}`}
                    value={formData.password}
                    onChange={(e) => updateField('password', e.target.value)}
                    onBlur={() => setTouchedFields((prev) => ({ ...prev, password: true }))}
                    required={!editingUser}
                  />
                  {touchedFields.password && formErrors.password && <p className="mt-1 text-xs text-red-600">{formErrors.password}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Role</label>
                  <select
                    className={`w-full px-3 py-2 border rounded bg-white ${touchedFields.role && formErrors.role ? 'border-red-500' : 'border-slate-300'}`}
                    value={formData.role}
                    onChange={(e) => updateField('role', e.target.value)}
                    onBlur={() => setTouchedFields((prev) => ({ ...prev, role: true }))}
                  >
                    {ROLE_OPTIONS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                  </select>
                  {touchedFields.role && formErrors.role && <p className="mt-1 text-xs text-red-600">{formErrors.role}</p>}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center gap-2"><FiServer /> user_site</label>
                {isSuperAdmin ? (
                  <div className="p-3 rounded border border-slate-200 bg-slate-50 text-sm text-slate-700">SuperAdmin có quyền toàn bộ site, không cần gán site.</div>
                ) : isSingleSiteRole ? (
                  <select
                    className={`w-full px-3 py-2 border rounded bg-white ${touchedFields.site_ids && formErrors.site_ids ? 'border-red-500' : 'border-slate-300'}`}
                    value={(formData.site_ids || [])[0] || ''}
                    onChange={(e) => updateField('site_ids', e.target.value ? [e.target.value] : [])}
                    onBlur={() => setTouchedFields((prev) => ({ ...prev, site_ids: true }))}
                  >
                    <option value="">Chọn 1 site</option>
                    {sites.map((site) => <option key={site.id} value={site.id}>{site.name}</option>)}
                  </select>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-52 overflow-y-auto pr-1">
                    {sites.map((site) => {
                      const isSelected = (formData.site_ids || []).includes(site.id);
                      return (
                        <label key={site.id} className="flex items-start gap-2 p-2 border border-slate-200 rounded hover:bg-slate-50">
                          <input type="checkbox" className="mt-1" checked={isSelected} onChange={() => toggleSite(site.id)} />
                          <span className="text-sm text-slate-800">{site.name}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
                {touchedFields.site_ids && formErrors.site_ids && <p className="mt-1 text-xs text-red-600">{formErrors.site_ids}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center gap-2"><FiGrid /> user_menu</label>
                {isSuperAdmin ? (
                  <div className="p-3 rounded border border-slate-200 bg-slate-50 text-sm text-slate-700">SuperAdmin có toàn bộ menu.</div>
                ) : (
                  <div className="space-y-3">
                    {MENU_SECTIONS.map((section) => {
                      // Only show keys that the server recognises (menuCatalog from API)
                      const sectionKeys = section.items.filter((item) => menuCatalog.includes(item.menuKey));
                      if (sectionKeys.length === 0) return null;
                      return (
                        <div key={section.title}>
                          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">{section.title}</p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {sectionKeys.map((item) => {
                              const checked = (formData.menu_keys || []).includes(item.menuKey);
                              return (
                                <label key={item.menuKey} className="flex items-center gap-2 p-2 border border-slate-200 rounded hover:bg-slate-50">
                                  <input type="checkbox" checked={checked} onChange={() => toggleMenu(item.menuKey)} />
                                  <span className="text-sm text-slate-700">{MENU_KEY_LABEL[item.menuKey] || item.menuKey}</span>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className={CRUD_UI.modalFooter}>
                <button type="button" className={CRUD_UI.secondaryButton} onClick={() => { setShowModal(false); resetForm(); }}>
                  <FiX className="text-base" />
                  Hủy
                </button>
                <button type="submit" className={CRUD_UI.primaryButton}>
                  <FiSave className="text-base" />
                  {editingUser ? 'Cập nhật' : 'Tạo'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
